'use client';
import { useState, useEffect } from 'react';

export interface BreakpointValues<T> {
  base?: T;
  sm?: T;
  md?: T;
  lg?: T;
  xl?: T;
  '2xl'?: T;
}

const breakpoints = {
  base: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

export function useBreakpointValue<T>(values: BreakpointValues<T> | T): T | undefined {
  // Always call hooks first to comply with Rules of Hooks
  const [currentBreakpoint, setCurrentBreakpoint] = useState<keyof typeof breakpoints>('base');

  useEffect(() => {
    const getBreakpoint = (): keyof typeof breakpoints => {
      const width = window.innerWidth;

      if (width >= breakpoints['2xl']) return '2xl';
      if (width >= breakpoints.xl) return 'xl';
      if (width >= breakpoints.lg) return 'lg';
      if (width >= breakpoints.md) return 'md';
      if (width >= breakpoints.sm) return 'sm';
      return 'base';
    };

    const handleResize = () => {
      setCurrentBreakpoint(getBreakpoint());
    };

    // Set initial value
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // If it's not an object, return it as is - checked after hooks to comply with Rules of Hooks
  if (typeof values !== 'object' || values === null) {
    return values as T;
  }

  // Get the value for the current breakpoint or fall back to smaller breakpoints
  const breakpointOrder: (keyof typeof breakpoints)[] = ['2xl', 'xl', 'lg', 'md', 'sm', 'base'];
  const currentIndex = breakpointOrder.indexOf(currentBreakpoint);

  for (let i = currentIndex; i < breakpointOrder.length; i++) {
    const breakpoint = breakpointOrder[i];
    if (breakpoint in values && (values as BreakpointValues<T>)[breakpoint] !== undefined) {
      return (values as BreakpointValues<T>)[breakpoint];
    }
  }

  return (values as BreakpointValues<T>).base;
}
